import React, { ReactElement, useContext, useEffect } from 'react';
import { IModalProps, Modal } from '@seismic/mantle';
import { Setup, SetupFooter } from './Setup';
import { Record, RecordFooter } from './Record';
import { Preview, PreviewFooter } from './Preview';
import { RecordingStateActions, WorkflowSteps } from '../models';
import useRecordingState from '../hooks/useRecordingState';
import { LoggingContext } from '../context';
import styles from './UniversalVideoRecorder.scss';
import isIphoneOrIpod from '../utilities/isIphoneOrIpod';
import isSafari from '../utilities/isSafari';
import Error from './Error/Error';
import ErrorFooter from './Error/ErrorFooter';
import isIE from '../utilities/isIE';
import { useTranslation } from 'react-i18next';

interface UniversalVideoRecorderProps extends Omit<IModalProps, 'onClose'> {
  open: boolean;
  maxRecordTimeSeconds?: number;
  onClose?: (video: Blob | null, filename?: string) => void;
  onProcessingComplete?: (content: string | null | Error) => void;
  onEvent?: () => void;
}

export default function UniversalVideoRecorder(props: UniversalVideoRecorderProps): ReactElement {
  const { t } = useTranslation();
  const { open } = props;
  const { state, dispatch } = useRecordingState();
  const { flush } = useContext(LoggingContext);
  const { recorder, fileName } = state;

  /**
   * Ensures the state of the component is reset
   */
  useEffect(() => {
    if (open) {
      dispatch({ type: RecordingStateActions.Initialize });
    }
  }, [open, dispatch]);

  /**
   * Ensures the state of the component is reset
   */
  useEffect(() => {
    if (!open) {
      recorder.dispose();
    }
  }, [open, recorder]);

  useEffect(() => {
    if (isIE()) {
      dispatch({ type: RecordingStateActions.HandleError });
    }
  }, [open, dispatch]);

  /**
   * Need to wire up the recording complete callback with the end recording action
   */
  useEffect(() => {
    recorder.onComplete = () => {
      dispatch({ type: RecordingStateActions.ChangeStep, payload: WorkflowSteps.Preview });
    };

    // recorder.onChunkAvailable = (chunk) => {
    //   // https://github.com/tus/tus-js-client/pull/126
    //   // Upload to server as data becomes available, instead of all at once at the end
    // };
  }, [recorder, dispatch]);

  /**
   * Pushes error logs to the server on close, if they exist
   */
  useEffect(() => {
    if (!open) {
      flush();
    }
  }, [flush, open]);

  function onCancel() {
    // eslint-disable-next-line
    props.onClose && props.onClose(null, fileName);
  }

  function getContent(): ReactElement {
    switch (state.step) {
      case WorkflowSteps.Setup:
        return <Setup state={state} dispatch={dispatch} />;
      case WorkflowSteps.Recording:
        return <Record state={state} dispatch={dispatch} />;
      case WorkflowSteps.Preview:
        return <Preview state={state} dispatch={dispatch} />;
      case WorkflowSteps.Error:
        return <Error />;
      default:
        return <div />;
    }
  }

  function getFooter(): ReactElement {
    switch (state.step) {
      case WorkflowSteps.Setup:
        return <SetupFooter state={state} dispatch={dispatch} onCancel={onCancel} />;
      case WorkflowSteps.Recording:
        return (
          <RecordFooter
            maxRecordTimeSeconds={props.maxRecordTimeSeconds}
            state={state}
            dispatch={dispatch}
            onCancel={onCancel}
          />
        );
      case WorkflowSteps.Preview:
        return (
          <PreviewFooter
            state={state}
            dispatch={dispatch}
            onCancel={onCancel}
            onUploadComplete={(video) => props.onClose && props.onClose(video, fileName)}
            onProcessingComplete={props.onProcessingComplete}
          />
        );
      case WorkflowSteps.Error:
        return <ErrorFooter />;
      default:
        return <div />;
    }
  }

  const iosSafariOnlyStyle = isIphoneOrIpod() && isSafari() ? styles.iosSafariOnly : '';

  return (
    <Modal
      className={`${styles.UniversalVideoRecorderModal} ${iosSafariOnlyStyle}`}
      data-testid="uvrModal"
      open={open}
      onClose={onCancel}
      header={t('Header.Title', { defaultValue: 'Create a recording' })}
      closeOnOutsideClick={false}
      size="lg"
      footer={getFooter()}
    >
      {getContent()}
    </Modal>
  );
}
